// frontend/src/services/auditorService.ts
// Servicio para interactuar con los endpoints del módulo Auditoría (/api/auditor/*)

import axiosInstance from '../config/axiosInstance';
import { 
    IngestionResponse, 
    AuditorAuditRecord, 
    CalculationPayload, 
    ReglaConcepto,
    ComplianceRequest,
    ComplianceResponse,
    AuditorFileNormalizationResult,
    ReportDownloadOptions
} from '../types/auditor';

// 1. INGESTA DE ARCHIVO (Upload + Validation)
export const ingestAuditFile = async (
    file: File, 
    clientName: string,
    customMapping?: Record<string, string>,
    normalization?: {
        sheet_name?: string | null;
        header_row_index?: number | null;
    } | null
): Promise<IngestionResponse> => {
    console.log(`[AuditorService] Uploading file: ${file.name}`);
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('client_name', clientName);
    
    if (customMapping) {
        formData.append('custom_mapping', JSON.stringify(customMapping));
    }

    if (normalization?.sheet_name) {
        formData.append('sheet_name', normalization.sheet_name);
    }

    if (
        normalization?.header_row_index !== null &&
        normalization?.header_row_index !== undefined
    ) {
        formData.append('header_row_index', String(normalization.header_row_index));
    }

    try {
        const response = await axiosInstance.post<IngestionResponse>('/auditor/ingest', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    } catch (err: any) {
        console.error('[AuditorService] Error ingesting file:', err);
        const errorDetail = err.response?.data?.detail || err.message || 'Error al subir el archivo de auditoría.';
        throw new Error(errorDetail);
    }
};

export const analyzeAuditorFileNormalization = async (
    file: File
): Promise<AuditorFileNormalizationResult> => {
    console.log(`[AuditorService] Analyzing auditor file normalization: ${file.name}`);

    const formData = new FormData();
    formData.append('file', file);

    try {
        const response = await axiosInstance.post<AuditorFileNormalizationResult>(
            '/auditor/file-normalization/analyze',
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            }
        );

        return response.data;
    } catch (err: any) {
        console.error('[AuditorService] Error analyzing auditor file normalization:', err);
        const errorDetail = err.response?.data?.detail || err.message || 'Error analizando la estructura del archivo.';
        throw new Error(errorDetail);
    }
};

export const reviewAuditorFileNormalization = async (
    file: File,
    headerRowIndex: number,
    sheetName?: string | null
): Promise<AuditorFileNormalizationResult> => {
    console.log(`[AuditorService] Reviewing auditor file normalization: ${file.name}`);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('header_row_index', String(headerRowIndex));

    if (sheetName && sheetName.trim()) {
        formData.append('sheet_name', sheetName.trim());
    }

    try {
        const response = await axiosInstance.post<AuditorFileNormalizationResult>(
            '/auditor/file-normalization/review',
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            }
        );

        return response.data;
    } catch (err: any) {
        console.error('[AuditorService] Error reviewing auditor file normalization:', err);
        const errorDetail = err.response?.data?.detail || err.message || 'Error revisando la normalización del archivo.';
        throw new Error(errorDetail);
    }
};

// 2. OBTENER DETALLE DE AUDITORÍA (Estado + Datos Brutos)
export const getAuditDetail = async (auditId: string): Promise<AuditorAuditRecord> => {
    console.log(`[AuditorService] Fetching audit detail: ${auditId}`);
    try {
        const response = await axiosInstance.get<AuditorAuditRecord>(`/auditor/${auditId}`);
        return response.data;
    } catch (err: any) {
        console.error(`[AuditorService] Error fetching audit ${auditId}:`, err);
        const errorDetail = err.response?.data?.detail || err.message || 'No se pudo recuperar la auditoría.';
        throw new Error(errorDetail);
    }
};

// 3. EJECUTAR CÁLCULOS MATEMÁTICOS (Promedios, Medianas, Equiparación)
export const calculateAuditStats = async (
    auditId: string,
    reglas: ReglaConcepto[],
    anioAnalisis: number
): Promise<any> => {
    console.log(`[AuditorService] Calculating stats for: ${auditId} (año ${anioAnalisis})`);

    const payload: CalculationPayload = {
        reglas,
        anio_analisis: anioAnalisis
    };

    try {
        const response = await axiosInstance.post(`/auditor/${auditId}/calculate`, payload);
        return response.data;
    } catch (err: any) {
        console.error(`[AuditorService] Error calculating stats for ${auditId}:`, err);
        const errorDetail = err.response?.data?.detail || err.message || 'Error en el motor de cálculo.';
        throw new Error(errorDetail);
    }
};

// 4. DESCARGAR INFORME LEGAL (Word / .docx)
export const downloadAuditReport = async (
    auditId: string,
    clientName: string,
    options: ReportDownloadOptions
): Promise<void> => {
    console.log(
        `[AuditorService] Downloading report for: ${auditId} ` +
        `(profile=${options.reportProfileId}, displayMode=${options.comparisonDisplayMode})`
    );

    try {
        // Solicitamos respuesta tipo BLOB (Binary Large Object)
        const response = await axiosInstance.get(`/auditor/${auditId}/report`, {
            responseType: 'blob',
            params: {
                report_profile_id: options.reportProfileId,
                comparison_display_mode: options.comparisonDisplayMode
            }
        });

        // Crear URL temporal para descarga
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;

        // Generar nombre de archivo limpio y distinguible durante la validación.
        const safeClientName = clientName.replace(/[^a-z0-9]/gi, '_').toLowerCase();

        const profileSuffix =
            options.reportProfileId === 'despacho_validado_v1'
                ? 'modelo_despacho'
                : 'modelo_ampliado';

        const displaySuffix =
            options.comparisonDisplayMode === 'percentages_only'
                ? 'porcentajes'
                : 'detallado';

        link.setAttribute(
            'download',
            `Informe_Auditoria_${safeClientName}_${profileSuffix}_${displaySuffix}_${new Date().toISOString().split('T')[0]}.docx`
        );

        document.body.appendChild(link);
        link.click();

        // Limpieza
        link.remove();
        window.URL.revokeObjectURL(url);
    } catch (err: any) {
        console.error(`[AuditorService] Error downloading report for ${auditId}:`, err);

        // Leer el mensaje del backend cuando la respuesta de error llega como Blob.
        if (err.response?.data instanceof Blob) {
            const errorText = await err.response.data.text();

            try {
                const errorJson = JSON.parse(errorText);
                throw new Error(errorJson.detail || 'Error generando el documento.');
            } catch (parseError: any) {
                if (parseError instanceof Error && parseError.message !== 'Error generando el documento.') {
                    throw parseError;
                }

                throw new Error('Error desconocido generando el documento.');
            }
        }

        const errorDetail = err.response?.data?.detail || err.message || 'Error generando el documento.';
        throw new Error(errorDetail);
    }
};

// 5. GUARDAR CHECKLIST DE CUMPLIMIENTO (Modo Premium)
export const upsertAuditCompliance = async (
  auditId: string,
  payload: ComplianceRequest
): Promise<any> => {
  try {
    const response = await axiosInstance.post(`/auditor/${auditId}/compliance`, payload);
    return response.data;
  } catch (err: any) {
    console.error(`[AuditorService] Error saving compliance for ${auditId}:`, err);
    const errorDetail = err.response?.data?.detail || err.message || 'Error guardando checklist de cumplimiento.';
    throw new Error(errorDetail);
  }
};

// 6. OBTENER CHECKLIST DE CUMPLIMIENTO (si existe)
export const getAuditCompliance = async (auditId: string): Promise<ComplianceResponse> => {
  try {
    const response = await axiosInstance.get<ComplianceResponse>(`/auditor/${auditId}/compliance`);
    return response.data;
  } catch (err: any) {
    console.error(`[AuditorService] Error fetching compliance for ${auditId}:`, err);
    const errorDetail = err.response?.data?.detail || err.message || 'No se pudo recuperar el checklist de cumplimiento.';
    throw new Error(errorDetail);
  }
};
